import type { CSSProperties } from 'react'
import { useHitTarget, type HitTargetSize } from '../responsive/hitTarget'

export type ButtonVariant = 'primary' | 'chip' | 'outline' | 'destructive' | 'selected'
export type ButtonSize = HitTargetSize

/**
 * The class list and the hit-target floor a button-skinned control needs —
 * in ONE place, consumed by both `Button` and `ButtonLink`.
 *
 * **This exists because reducing the copy count was not the same as fixing the
 * bug.** Issue #334 was four screens hand-copying `Button`'s class list and its
 * inline `min-height`, one of which omitted the `min-height` and shipped 42.8px
 * controls against M0-R29's 44px floor. The first fix introduced `ButtonLink`
 * and took four copies to two — `Button` and `ButtonLink`, each independently
 * building the same list in the same order and each independently applying
 * `useHitTarget(size)`. The coupling between them was then stated as a rule in
 * a docblock, which is precisely the mechanism that had just failed: #334's own
 * root cause was a correct rule written down and not enforced.
 *
 * `hitTarget.ts` records paying for this once already — `Sidebar` kept a private
 * `44 : 33` and #90 would have had to be applied twice without noticing the
 * second. Two copies is how that starts.
 *
 * **The ORDER of the classes is load-bearing, not cosmetic.** `hoverable` is
 * what `motion.css` transitions and what `primitives.css` gives `:hover` rules
 * to, so dropping it makes both inert; and a caller's `className` is MERGED
 * last rather than assigned over, because a call site passing `className="enter"`
 * for the 280ms entry animation would otherwise drop the skin and the focus
 * ring. Both were separately learned and both now have one implementation.
 *
 * **`minHeight` is composed after the caller's `style`, deliberately.** It is
 * the one property a caller must not be able to override: the floor has to win
 * the cascade, and `min-height` also beats `height`, so it genuinely holds
 * rather than merely appearing to. Everything else about the skin — fills,
 * boundaries, hover, focus — stays in `primitives.css`, because an inline style
 * beats every selector and a fill written here could not be overridden by a
 * `:hover` rule at any specificity. That is not theoretical: it happened twice.
 *
 * A hook, not a plain function, because `useHitTarget` reads `useBreakpoint()`.
 * That is the single breakpoint source CLAUDE.md names, and a media query
 * cannot do this job — #90 measured an inline declaration beating one at 375px.
 */
export function useButtonSkin(
  variant: ButtonVariant,
  size: ButtonSize,
  className: string | undefined,
  style: CSSProperties | undefined,
): { className: string; style: CSSProperties } {
  const minHeight = useHitTarget(size)
  return {
    className: ['btn', `btn--${variant}`, `btn--size-${size}`, 'hoverable', className]
      .filter(Boolean)
      .join(' '),
    style: { ...style, minHeight },
  }
}
